import { base64UrlToJson } from '~/utils/base64-utils';

export const LS_KEYS = {
  gptSelectedTransaction: 'gptSelectedTransaction',
};

/**
 * List of available option types
 */
export const OPTION_TYPES = {
  quickChat: 'quick_chat',
  transactions: 'transactions',
};

/**
 * Available `system_commands` in the Mitra Chat GPT system
 */
export const SYSTEM_COMMANDS = {
  escalated_to_acha: 'escalated_to_acha',
  escalated_to_tier_1: 'escalated_to_tier_1',
  disable_typing_area: 'disable_typing_area',
  close_session: 'close_session',
  ask_for_feedback: 'ask_for_feedback',
  enable_upload_button: 'enable_upload_button',
  choose_vp_transactions: 'choose_vp_transactions',
};

/**
 * Contains system_command - method name mappings
 */
export const SYSTEM_COMMAND_METHOD_MAPPING = {
  escalated_to_acha: 'escalateToAcha',
  escalated_to_tier_1: 'escalateToTier1Agent', // simply display the button to escalate to tier 1 CS agent
  disable_typing_area: 'disableTypingArea', // this would disable typing area by command
  close_session: 'closeSession', // this would end the current session
  ask_for_feedback: 'askForFeedback', // this would show the closing options for feedback
  enable_upload_button: 'enableUploadButton', // this would show "Upload Foto" button
  choose_vp_transactions: 'chooseVpTransactions', // this would stop the current session if the GPT can't return options
};

export const FEEDBACK_MESSAGES = {
  positive: 'Solusi sudah menjawab pertanyaan saya',
  negative: 'Saya ingin dihubungkan dengan CS',
};

export const FEEDBACK_CLOSING_MESSAGES = {
  positive: 'Terima kasih banyak, jawaban yang kakak berikan membantu Acha untuk semakin baik ke depannya.',
  negative: {
    msg1: 'Baik, silakan klik tombol Panggil CS di bawah agar dibantu teman Acha di Bantuan Mitra, ya',
    msg2:
      'Mohon untuk tidak keluar dari halaman ini sebelum masalahmu selesai. Jika tidak, sesi Live Chat akan ditutup otomatis dalam 5 menit.',
  },
};

export const TRANSACTION_MESSAGE_TEMPLATE =
  'Halo, saya butuh bantuan mengenai <product_type> dengan nomor transaksi <payment_id>';

export const SELECTED_TRANSACTION_OPTION_MESSAGE_TEMPLATE =
  'Saya butuh bantuan mengenai <product_type>. Berikut nomor transaksinya <payment_id>';

export const TRANSACTION_NOT_FOUND_USER_MESSAGE = 'Transaksi tidak ditemukan';

export const TRANSACTION_NOT_FOUND_GPT_MESSAGE =
  'Baik, silakan klik tombol di bawah untuk diarahkan ke halaman transaksi. Juragan bisa memilih transaksi juragan yang bermasalah, ya.';

export const TRANSACTION_CONTEXT_SCHEMA = {
  invoice_group_number: '',
  transaction_id: 0,
  transaction_type: '',
  transaction_name: '',
  status: '',
  amount: 0,
  metadata: '',
  created_at: '',
};

/**
 * This function will be used to optimistically add new message object to the `messages` array
 * @param {object} payload
 * @param {string} payload.content
 * @param {string} payload.attachment
 * @param {object} payload.option
 * @param {string} payload.systemCommand
 * @param {boolean} payload.isGptReply
 * @returns {object}
 */
export const composeMessageObject = ({
  content = '',
  attachment = '',
  option = null,
  systemCommand = '',
  isGptReply = false,
}) => {
  const date = new Date();
  const timestamp = date.getTime(); // use this for temporary message id
  const createdAt = date.toISOString();

  return {
    id: timestamp,
    content,
    attachment,
    option,
    system_command: systemCommand,
    ref_chat_id: isGptReply ? 999 : null,
    created_at: createdAt,
  };
};

/**
 * This function will be used to create a greeting message object
 * @returns {object}
 */
export const composeGreetingMessageObject = () => {
  const message = composeMessageObject({
    content: 'Halo Juragan! Namaku Acha, Asisten virtual chat BukaBantuan. Apa yang bisa aku bantu hari ini?',
    isGptReply: true,
  });

  return message;
};

/**
 * This function would allow the Mitra Chat GPT to display a feedback options message
 * @returns {object}
 */
export const composeFeedbackOptionsMessageObject = () => {
  const message = composeMessageObject({
    option: {
      type: OPTION_TYPES.quickChat,
      data: [{ value: FEEDBACK_MESSAGES.positive }, { value: FEEDBACK_MESSAGES.negative }],
    },
    isGptReply: true,
  });

  return message;
};

/**
 * This function will generate an arbitrary closing message based on the selected feedback option
 * @param {boolean} hasPositiveSentiment
 * @returns {object[]}
 */
export const composeFeedbackClosingMessageObject = (hasPositiveSentiment = false) => {
  if (hasPositiveSentiment) {
    const message = composeMessageObject({
      content: FEEDBACK_CLOSING_MESSAGES.positive,
      systemCommand: SYSTEM_COMMANDS.close_session,
      isGptReply: true,
    });
    return [message];
  }

  const message1 = composeMessageObject({ content: FEEDBACK_CLOSING_MESSAGES.negative.msg1, isGptReply: true });
  const message2 = composeMessageObject({
    content: FEEDBACK_CLOSING_MESSAGES.negative.msg2,
    systemCommand: SYSTEM_COMMANDS.escalated_to_tier_1,
    isGptReply: true,
  });
  return [message1, message2];
};

/**
 * @param {string} base64Transaction - selected transaction object in a base64 format
 * @returns {object}
 */
export const composeTransactionMessageObject = (base64Transaction = '') => {
  if (!base64Transaction) return null;

  const parsedTrx = base64UrlToJson(base64Transaction);
  const message = TRANSACTION_MESSAGE_TEMPLATE.replace('<product_type>', parsedTrx.productType).replace(
    '<payment_id>',
    parsedTrx.paymentId
  );

  return composeMessageObject({ content: message });
};

/**
 * @param {object} selectedTrx - selected transaction option
 * @returns {object}
 */
export const composeTransactionMessageObjectFromOption = selectedTrx => {
  if (!selectedTrx) return null;

  const message = SELECTED_TRANSACTION_OPTION_MESSAGE_TEMPLATE.replace(
    '<product_type>',
    selectedTrx.transaction_name
  ).replace('<payment_id>', selectedTrx.invoice_id || selectedTrx.invoice_group_number);

  return composeMessageObject({ content: message });
};

/**
 * @returns {object[]}
 */
export const composeTrxNotFoundMessageObject = () => {
  const userMessage = composeMessageObject({
    content: TRANSACTION_NOT_FOUND_USER_MESSAGE,
  });
  const gptMessage = composeMessageObject({
    content: TRANSACTION_NOT_FOUND_GPT_MESSAGE,
    isGptReply: true,
  });

  return [userMessage, gptMessage];
};

/**
 * Compose context object to be sent along with the first transaction template message
 * This would be called when the user has selected transaction from the transaction list or Transaksi Bermasalah page
 * @param {string} base64Transaction - selected transaction object in a base64 format
 * @returns {TRANSACTION_CONTEXT_SCHEMA}
 */
export const composeContextFromInitialSelectedTrx = base64Transaction => {
  const parsedTrx = base64UrlToJson(base64Transaction);

  return {
    ...TRANSACTION_CONTEXT_SCHEMA,
    invoice_group_number: parsedTrx.paymentId,
    transaction_id: parsedTrx.detailId,
    transaction_type: parsedTrx.trxType,
    transaction_name: parsedTrx.productType,
    status: parsedTrx.status,

    /**
     * The selected transaction from Transaksi Bermasalah and List Transaksi page
     * Does not have these items
     */
    // amount: 0,
    // metadata: '',
    // created_at: '',
  };
};
